import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Battle Contract & Rules — the official terms an entrant agrees to before
 * entering an IAMMUZIK Arena battle. Includes the red, all-caps standby rule
 * (top 5 on standby 2h before the live finale; unreachable = next in line),
 * the collab $15 add-on, the no-refund policy, monthly judge codes, and the
 * per-genre finalist codes.
 */
const SCORECARD_CRITERIA = [
  { k: 'Originality', body: 'Is the track a fresh idea, not a recycled trend or obvious clone?' },
  { k: 'Lyrics', body: 'Wordplay, storytelling, structure, and meaning. (Remember: the artist wrote these — AI never does.)' },
  { k: 'Vocal Delivery', body: 'Presence, emotion, timing, and control.' },
  { k: 'Production', body: 'Beat, mix, arrangement, and overall sonic quality.' },
  { k: 'Overall Impact', body: 'Does the song move the room? The "goosebumps" factor.' },
];

const FINALE_STAGES = [
  { wk: 'Weeks 1–3', title: 'Eliminations', body: 'Open submissions, then weekly cuts. The field narrows each week based on judge scores + audience vote.' },
  { wk: 'Week 4', title: 'Live Finale', body: 'Top 3 contenders go live on video. Judges flip score cards live. Audience polls in real time. Winner\'s song plays live.' },
];

export function BattleRulesPage() {
  const navigate = useNavigate();
  const [ackStandby, setAckStandby] = useState(false);
  const [ackNoRefund, setAckNoRefund] = useState(false);
  const [ackCollab, setAckCollab] = useState(false);
  const [ackCodes, setAckCodes] = useState(false);

  const allAck = ackStandby && ackNoRefund && ackCollab && ackCodes;

  return (
    <div className="grid">
      <div className="card">
        <div className="section-title" style={{ margin: 0 }}>Battle Contract & Official Rules</div>
        <p className="muted" style={{ fontSize: 14, marginTop: 8 }}>
          Every entrant must read and accept these terms before entering an Arena battle.
          IAMMUZIK is a skill-based competition — ability decides who advances. Not a lottery.
        </p>
        <div className="flex gap-8 wrap mt-16" style={{ marginTop: 14 }}>
          <a href="#standby" onClick={(e) => { e.preventDefault(); document.getElementById('standby')?.scrollIntoView({behavior:'smooth'}); }} className="btn btn-outline btn-sm">Standby Rule</a>
          <a href="#collabs" onClick={(e) => { e.preventDefault(); document.getElementById('collabs')?.scrollIntoView({behavior:'smooth'}); }} className="btn btn-outline btn-sm">Collab Fee</a>
          <a href="#refunds" onClick={(e) => { e.preventDefault(); document.getElementById('refunds')?.scrollIntoView({behavior:'smooth'}); }} className="btn btn-outline btn-sm">No Refunds</a>
          <a href="#codes" onClick={(e) => { e.preventDefault(); document.getElementById('codes')?.scrollIntoView({behavior:'smooth'}); }} className="btn btn-outline btn-sm">Access Codes</a>
          <a href="#scorecard" onClick={(e) => { e.preventDefault(); document.getElementById('scorecard')?.scrollIntoView({behavior:'smooth'}); }} className="btn btn-outline btn-sm">Scorecards</a>
          <a href="#structure" onClick={(e) => { e.preventDefault(); document.getElementById('structure')?.scrollIntoView({behavior:'smooth'}); }} className="btn btn-outline btn-sm">Finale Structure</a>
        </div>
      </div>

      {/* ===================== STANDBY RULE — RED, ALL CAPS ===================== */}
      <div className="card" id="standby" style={{ borderColor: 'rgba(255,107,129,0.45)', background: 'rgba(255,107,129,0.05)' }}>
        <div className="section-title" style={{ color: '#ff7a8a', margin: 0 }}>
          ⚠ TOP 5 STANDBY RULE — READ CAREFULLY ⚠
        </div>
        <p style={{ color: '#ff9aa6', fontWeight: 500, fontSize: 16, marginTop: 12, textTransform: 'uppercase', letterSpacing: '0.6px', lineHeight: 1.9, fontFamily: 'Inter, system-ui, sans-serif' }}>
          The top 5 ranked artists must be on standby two (2) hours prior to the live choosing of the winner.
          If IAMMUZIK cannot reach an artist in that window, that artist forfeits their finalist slot and the
          next artist in line takes their place — #4 moves into #3's slot, #5 into #4's, and the next ranked
          entrant becomes #5. This continues down the ranking until a reachable artist fills every slot.
          No exceptions. No excuses. Being unreachable costs you the crown.
        </p>
        <label className="checkbox-row" style={{ borderColor: 'rgba(255,107,129,0.4)', background: 'rgba(255,107,129,0.05)' }}>
          <input type="checkbox" checked={ackStandby} onChange={(e) => setAckStandby(e.target.checked)} />
          <span style={{ color: '#ffb3bc', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.3px', fontFamily: 'Inter, system-ui, sans-serif' }}>
            I understand the Top 5 Standby Rule. If I cannot be reached 2 hours before the live finale, I forfeit my slot and the next artist in line takes my place.
          </span>
        </label>
      </div>

      {/* ===================== COLLAB FEE ===================== */}
      <div className="card" id="collabs">
        <h3 style={{ marginBottom: 8 }}>Collaborations & Featured-Artist Fee</h3>
        <p className="muted" style={{ fontSize: 14, margin: 0, lineHeight: 1.7 }}>
          Tracks that feature other artists ARE allowed in battles — but payment rules are strict:
        </p>
        <ul style={{ color: 'var(--iam-muted)', fontSize: 14, lineHeight: 1.8, marginTop: 10, paddingLeft: 20 }}>
          <li>Every featured artist must have their own IAMMUZIK account that is <strong>paid</strong>, not free.</li>
          <li>Both the primary artist and the featured artist must be present for the battle.</li>
          <li>There is an <strong>additional $15 fee</strong> when more than one artist is featured on the track.</li>
          <li>A registered group counts as a single artist — no per-member fee for groups.</li>
          <li>Misrepresenting a collaboration (e.g. listing a feature that doesn't exist, or hiding one) is grounds for <strong>immediate disqualification</strong>.</li>
        </ul>
        <label className="checkbox-row">
          <input type="checkbox" checked={ackCollab} onChange={(e) => setAckCollab(e.target.checked)} />
          <span>I understand the featured-artist and $15 collab-fee rules.</span>
        </label>
      </div>

      {/* ===================== NO REFUNDS ===================== */}
      <div className="card" id="refunds">
        <h3 style={{ marginBottom: 8 }}>No-Refund Policy</h3>
        <p className="muted" style={{ fontSize: 14, margin: 0, lineHeight: 1.7 }}>
          <strong>Battle entry fees are non-refundable. Period.</strong> No refunds are issued for lost battles,
          changed mind, schedule conflicts, or disqualification for rule violations. The only exception is a
          verified wrongful charge or a platform error — in which case, contact support and the charge will be
          reviewed and reversed if confirmed.
        </p>
        <label className="checkbox-row">
          <input type="checkbox" checked={ackNoRefund} onChange={(e) => setAckNoRefund(e.target.checked)} />
          <span>I understand that battle entry fees are non-refundable except in cases of verified wrongful charge.</span>
        </label>
      </div>

      {/* ===================== ACCESS CODES ===================== */}
      <div className="card" id="codes" style={{ borderColor: 'rgba(36,84,245,0.4)' }}>
        <h3 style={{ marginBottom: 8, color: 'var(--iam-royal-blue)' }}>Live Access Codes</h3>
        <p className="muted" style={{ fontSize: 14, margin: 0, lineHeight: 1.7 }}>
          Live finale access is gated by rotating codes so only cleared contestants and judges enter the room:
        </p>
        <ul style={{ color: 'var(--iam-muted)', fontSize: 14, lineHeight: 1.8, marginTop: 10, paddingLeft: 20 }}>
          <li><strong>Judge codes</strong> — a platform-wide code generated monthly by IAMMUZIK. Each judge must enter it to access scoring. It rotates every month.</li>
          <li><strong>Finalist codes (per genre)</strong> — the top 3 in each genre (Hip-Hop, Country, Gospel, etc.) receive one shared code for that event's live feed. Each genre gets its own code. Codes rotate monthly.</li>
          <li>Codes are single-use per event and never reused across months. Treat your code like a ticket — sharing it can disqualify your entry.</li>
        </ul>
        <label className="checkbox-row">
          <input type="checkbox" checked={ackCodes} onChange={(e) => setAckCodes(e.target.checked)} />
          <span>I understand the monthly judge and per-genre finalist access-code system.</span>
        </label>
      </div>

      {/* ===================== SCORECARDS ===================== */}
      <div className="card" id="scorecard">
        <h3 style={{ marginBottom: 8 }}>Judges' Scorecards</h3>
        <p className="muted" style={{ fontSize: 14, margin: 0, lineHeight: 1.7 }}>
          Judges score each entry on five criteria from 1–10. Scoring is live during the finale — judges flip
          their score cards in real time. Once every assigned judge submits, the battle auto-finalizes.
          Final score = 70% judges / 30% verified audience vote.
        </p>
        <div className="grid cols-3 mt-16" style={{ marginTop: 16 }}>
          {SCORECARD_CRITERIA.map((c) => (
            <div key={c.k} className="card" style={{ padding: 16 }}>
              <div className="section-title gold" style={{ fontSize: 14 }}>{c.k}</div>
              <p className="muted" style={{ fontSize: 13, margin: '8px 0 0', lineHeight: 1.5 }}>{c.body}</p>
            </div>
          ))}
        </div>
      </div>

      {/* ===================== FINALE STRUCTURE ===================== */}
      <div className="card" id="structure">
        <h3 style={{ marginBottom: 8 }}>Finale Structure</h3>
        <div className="grid cols-2 mt-16" style={{ marginTop: 14 }}>
          {FINALE_STAGES.map((s) => (
            <div key={s.wk} className="card" style={{ padding: 16 }}>
              <span className="chip chip-approved" style={{ marginBottom: 8 }}>{s.wk}</span>
              <div className="section-title" style={{ fontSize: 16 }}>{s.title}</div>
              <p className="muted" style={{ fontSize: 13, margin: '8px 0 0', lineHeight: 1.5 }}>{s.body}</p>
            </div>
          ))}
        </div>
        <div className="policy-banner" style={{ marginTop: 16 }}>
          <strong>Note on live video:</strong> Full live-battle video is coming soon. Producing it right requires
          per-contestant recording from a computer (not a phone), green-screen or controlled scenery so viewers
          don't see people, kids, or background activity walking through frame, and a hardened live room. The
          scoring, codes, and finale mechanics above are live now; the live video feed rolls out once those
          production standards are met.
        </div>
      </div>

      {/* ===================== ACCEPTANCE ===================== */}
      <div className="card" style={{ borderColor: allAck ? 'rgba(67,122,34,0.5)' : 'var(--border)' }}>
        <div className="flex gap-8 wrap" style={{ alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div className="section-title" style={{ margin: 0, color: allAck ? 'var(--iam-success)' : undefined }}>
              {allAck ? '✓ All terms acknowledged' : 'Acknowledge all terms to enter'}
            </div>
            <p className="muted" style={{ fontSize: 13, marginTop: 6 }}>
              {ackStandby ? '✓' : '○'} Standby &nbsp; {ackCollab ? '✓' : '○'} Collab fee &nbsp; {ackNoRefund ? '✓' : '○'} No refunds &nbsp; {ackCodes ? '✓' : '○'} Access codes
            </p>
          </div>
          <button className="btn btn-gold" disabled={!allAck} onClick={() => navigate('/app/arena')}>
            Accept & Enter Battle
          </button>
        </div>
      </div>
    </div>
  );
}
